import { applyCentralDifference } from "../filters/centralDiff";
import { applySavgol } from "../filters/savgol";

import type { ASTNode } from "./parser";

/**
 * Registry entry for a function callable from the formula language.
 *
 * `kind: "scalar"` — point-wise `impl(...numbers) => number`. Arity is not
 * validated per name, so `sin(1, 2, 3)` reaches `Math.sin`, which ignores extras.
 *
 * `kind: "windowed"` — `impl(values, times, ...rest) => number[]` consumes the
 * whole series at once. It owns window/order resolution and short-series
 * short-circuiting, keeping the series-mode evaluator kernel-agnostic.
 */
export type MathFunction =
  | { kind: "scalar"; impl: (...args: number[]) => number }
  | {
      kind: "windowed";
      impl: (values: number[], times: number[], ...rest: number[]) => number[];
    };

// Scalar built-ins and windowed kernels share one registry; dispatch is on
// `kind` rather than parallel maps.
const MATH_FUNCTIONS: Record<string, MathFunction> = {
  abs: { kind: "scalar", impl: Math.abs },
  sqrt: { kind: "scalar", impl: Math.sqrt },
  pow: { kind: "scalar", impl: Math.pow },
  sin: { kind: "scalar", impl: Math.sin },
  cos: { kind: "scalar", impl: Math.cos },
  tan: { kind: "scalar", impl: Math.tan },
  log: { kind: "scalar", impl: Math.log },
  exp: { kind: "scalar", impl: Math.exp },
  min: { kind: "scalar", impl: Math.min },
  max: { kind: "scalar", impl: Math.max },
  round: { kind: "scalar", impl: Math.round },
  floor: { kind: "scalar", impl: Math.floor },
  ceil: { kind: "scalar", impl: Math.ceil },
  rad: { kind: "scalar", impl: (x: number) => x * (Math.PI / 180) },
  deg: { kind: "scalar", impl: (x: number) => x * (180 / Math.PI) },
  savgol: {
    kind: "windowed",
    impl: (values, times, windowSeconds, polynomial) => {
      const medianDt = medianDelta(times);
      if (!Number.isFinite(medianDt) || medianDt <= 0) {
        // Catch degenerate timestamps (out-of-order, duplicate, or non-finite)
        // here; left alone they yield Infinity/NaN window sizes downstream.
        throw new Error(
          `savgol: cannot resolve a window from non-monotonic or degenerate timestamps (median Δt = ${medianDt})`,
        );
      }
      const windowSize = resolveSavgolWindow(
        windowSeconds,
        medianDt,
        polynomial,
      );
      if (values.length < windowSize) {
        // Too few samples to fit the kernel — pass through untransformed.
        return [...values];
      }
      return applySavgol(values, times, windowSize, polynomial);
    },
  },
  deriv: {
    kind: "windowed",
    impl: (values, times, n) => {
      // validateAst pins `n` to 1 or 2; re-check at runtime rather than cast
      // `as 1 | 2`, so bypassed validation errors clearly instead of feeding
      // the stencil a bad order.
      if (n !== 1 && n !== 2) {
        throw new Error(`deriv: order must be 1 or 2 (got ${n})`);
      }
      const minLength = n === 1 ? 2 : 3;
      if (values.length < minLength) {
        return [...values];
      }
      return applyCentralDifference(values, times, n);
    },
  },
};

/** True if `name` resolves to a windowed entry in {@link MATH_FUNCTIONS}. */
function isWindowedFunction(name: string): boolean {
  return MATH_FUNCTIONS[name]?.kind === "windowed";
}

/** Look up a registry entry by name. Returns `undefined` for unknown names. */
export function lookupMathFunction(name: string): MathFunction | undefined {
  return MATH_FUNCTIONS[name];
}

// Resolved before `variables` so user bindings cannot shadow them.
const MATH_CONSTANTS: Record<string, number> = {
  PI: Math.PI,
  E: Math.E,
};

// Shared by scalar `evaluate` and the `evaluateAsSeries` broadcast helpers so
// the two paths can't diverge on NaN/Infinity: a scalar `1/0` and a broadcast
// `1/0` run the same `(a, b) => a / b`.
const BINARY_OPS: Record<string, (a: number, b: number) => number> = {
  "+": (a, b) => a + b,
  "-": (a, b) => a - b,
  "*": (a, b) => a * b,
  "/": (a, b) => a / b,
  "%": (a, b) => a % b,
  "^": Math.pow,
};

/** True if any Identifier in the AST has name `y`. */
export function referencesY(node: ASTNode): boolean {
  switch (node.type) {
    case "Literal":
      return false;
    case "Identifier":
      return node.name === "y";
    case "BinaryExpression":
      return referencesY(node.left) || referencesY(node.right);
    case "UnaryExpression":
      return referencesY(node.argument);
    case "CallExpression":
      return node.arguments.some(referencesY);
    default: {
      // Compile error on a new ASTNode variant, forcing an explicit case rather
      // than a falsy default that would misclassify its y-reference.
      const _exhaustive: never = node;
      throw new Error(`Unhandled AST node type: ${String(_exhaustive)}`);
    }
  }
}

/** True if any CallExpression in the AST resolves to a windowed registry entry. */
export function containsWindowedCall(node: ASTNode): boolean {
  switch (node.type) {
    case "Literal":
    case "Identifier":
      return false;
    case "BinaryExpression":
      return (
        containsWindowedCall(node.left) || containsWindowedCall(node.right)
      );
    case "UnaryExpression":
      return containsWindowedCall(node.argument);
    case "CallExpression":
      return (
        isWindowedFunction(node.callee) ||
        node.arguments.some(containsWindowedCall)
      );
    default: {
      // Compile error on a new ASTNode variant, forcing an explicit case rather
      // than silently routing a formula containing it down the scalar path.
      const _exhaustive: never = node;
      throw new Error(`Unhandled AST node type: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Evaluate a single AST node against `variables`. Non-finite results
 * (NaN, +/-Infinity) are returned as-is; the caller decides how to surface
 * them.
 *
 * When `windowedCache` is supplied, CallExpression nodes present in the
 * cache resolve to `cache.get(node)[index]` — `index` is required in that
 * case. Without a cache, any windowed CallExpression throws.
 */
export function evaluate(
  node: ASTNode,
  variables: Record<string, number>,
  windowedCache?: ReadonlyMap<ASTNode, readonly number[]>,
  index?: number,
): number {
  switch (node.type) {
    case "Literal":
      return node.value;

    case "Identifier":
      if (node.name in MATH_CONSTANTS) {
        return MATH_CONSTANTS[node.name];
      }
      if (node.name in variables) {
        return variables[node.name];
      }
      throw new Error(`Undefined variable: '${node.name}'`);

    case "BinaryExpression": {
      const op = BINARY_OPS[node.operator];
      if (!op) {
        throw new Error(`Unknown operator: '${node.operator}'`);
      }
      const left = evaluate(node.left, variables, windowedCache, index);
      const right = evaluate(node.right, variables, windowedCache, index);
      return op(left, right);
    }

    case "UnaryExpression": {
      // The AST type pins `operator: "-"`; the runtime check only guards callers
      // that hand-build nodes and bypass the types, which would otherwise get
      // unary minus applied to an unknown operator.
      const operator: string = node.operator;
      if (operator === "-") {
        return -evaluate(node.argument, variables, windowedCache, index);
      }
      throw new Error(`Unknown unary operator: '${operator}'`);
    }

    case "CallExpression": {
      if (windowedCache?.has(node)) {
        // A cached windowed result must be indexed. `cache` and `index` are
        // separate optional params, so guard against a cache passed without one.
        if (index === undefined) {
          throw new Error(
            "Cached windowed result requires an index when evaluated",
          );
        }
        return windowedCache.get(node)![index];
      }
      const entry = MATH_FUNCTIONS[node.callee];
      if (!entry) {
        throw new Error(`Undefined function: '${node.callee}'`);
      }
      if (entry.kind === "windowed") {
        throw new Error(
          `'${node.callee}' is a windowed function and cannot be evaluated point-by-point; use the series evaluator`,
        );
      }
      const args = node.arguments.map((arg) =>
        evaluate(arg, variables, windowedCache, index),
      );
      return entry.impl(...args);
    }
  }
}

/**
 * Series-mode evaluation. Walks `ast` bottom-up; each node yields either a
 * scalar `number` or a `number[]` of the same length as `values`.
 *
 * Operand shapes: literals and constant identifiers (`PI`, `E`) stay scalar;
 * `y` is the `values` array; binary/unary ops broadcast over mixed
 * (scalar, array) operands; scalar functions broadcast elementwise over any
 * array argument; windowed calls always return arrays. A windowed call's
 * non-first args (e.g. savgol's `windowSeconds`, `polynomial`) are pre-bound in
 * `constants` by {@link validateAst}, so constant subtrees are never re-walked.
 *
 * Precondition: `constants` must come from a successful {@link validateAst} on
 * this same `ast`, with an entry per windowed-call node. An incomplete map
 * throws rather than calling a kernel with `undefined` arguments.
 *
 * The return is always `number[]`: this path runs only on windowed formulas,
 * and any node below a windowed call broadcasts to an array, so the top level
 * is already an array. A scalar top level is promoted as a defensive no-op.
 */
export function evaluateAsSeries(
  ast: ASTNode,
  values: number[],
  times: number[],
  constants: ReadonlyMap<ASTNode, readonly number[]>,
): number[] {
  if (times.length !== values.length) {
    throw new Error(
      `evaluateAsSeries: values (${values.length}) and times (${times.length}) must have the same length`,
    );
  }

  function evalNode(node: ASTNode): number | number[] {
    switch (node.type) {
      case "Literal":
        return node.value;
      case "Identifier":
        if (node.name === "y") {
          return values;
        }
        if (node.name in MATH_CONSTANTS) {
          return MATH_CONSTANTS[node.name];
        }
        throw new Error(`Undefined variable: '${node.name}'`);
      case "BinaryExpression": {
        const op = BINARY_OPS[node.operator];
        if (!op) {
          throw new Error(`Unknown operator: '${node.operator}'`);
        }
        return broadcastBinary(evalNode(node.left), evalNode(node.right), op);
      }
      case "UnaryExpression": {
        const operator: string = node.operator;
        if (operator !== "-") {
          throw new Error(`Unknown unary operator: '${operator}'`);
        }
        return broadcastUnary(evalNode(node.argument), (x) => -x);
      }
      case "CallExpression": {
        const entry = MATH_FUNCTIONS[node.callee];
        if (!entry) {
          throw new Error(`Undefined function: '${node.callee}'`);
        }
        if (entry.kind === "windowed") {
          const firstArg = evalNode(node.arguments[0]);
          if (!Array.isArray(firstArg)) {
            // Validation enforces a y-reference inside the first argument, so
            // it always evaluates to an array. Reaching here means a validation
            // gap, not a user error.
            throw new Error(
              `internal: windowed first argument to '${node.callee}' did not evaluate to a series`,
            );
          }
          const rest = constants.get(node);
          if (rest === undefined) {
            // A missing entry means validateAst didn't run first (see the
            // precondition). Fail loudly rather than call the kernel with
            // `undefined` window/order args.
            throw new Error(
              `internal: windowed call '${node.callee}' has no pre-validated constants; run validateAst before evaluateAsSeries`,
            );
          }
          return entry.impl(firstArg, times, ...rest);
        }
        const args = node.arguments.map(evalNode);
        return broadcastScalarFn(entry.impl, args);
      }
    }
  }

  const result = evalNode(ast);
  if (Array.isArray(result)) {
    return result;
  }
  // Defensive no-op: the top level is always already an array (see docstring).
  return Array.from({ length: values.length }, () => result);
}

/**
 * Apply a binary op to two operands, each a scalar or a same-length array,
 * NumPy-style: a scalar is stretched to apply elementwise across an array, two
 * arrays combine elementwise (equal length required), two scalars combine to a
 * scalar. E.g. `9.81 * savgol(y, ...)` broadcasts the scalar 9.81 over every
 * element of the savgol output.
 */
function broadcastBinary(
  left: number | number[],
  right: number | number[],
  op: (a: number, b: number) => number,
): number | number[] {
  if (typeof left === "number" && typeof right === "number") {
    return op(left, right);
  }
  if (typeof left === "number") {
    return (right as number[]).map((r) => op(left, r));
  }
  if (typeof right === "number") {
    return left.map((l) => op(l, right));
  }
  if (left.length !== right.length) {
    throw new Error(
      `series-mode operand length mismatch (${left.length} vs ${right.length})`,
    );
  }
  return left.map((l, i) => op(l, right[i]));
}

function broadcastUnary(
  arg: number | number[],
  op: (x: number) => number,
): number | number[] {
  return typeof arg === "number" ? op(arg) : arg.map(op);
}

function broadcastScalarFn(
  impl: (...args: number[]) => number,
  args: (number | number[])[],
): number | number[] {
  const firstArray = args.find((a): a is number[] => Array.isArray(a));
  if (firstArray === undefined) {
    return impl(...(args as number[]));
  }
  const len = firstArray.length;
  for (const a of args) {
    if (Array.isArray(a) && a.length !== len) {
      throw new Error(
        `series-mode operand length mismatch (${a.length} vs ${len})`,
      );
    }
  }
  return Array.from({ length: len }, (_, i) =>
    impl(...args.map((a) => (Array.isArray(a) ? a[i] : a))),
  );
}

/**
 * Convert a window length in seconds to an odd sample count, floored at
 * `max(5, order + 1)`.
 *
 * `order + 1` is the Savitzky-Golay least-squares minimum. The `5` matches the
 * hard floor {@link applySavgol} enforces because the underlying `sgg` library
 * rejects smaller windows.
 */
function resolveSavgolWindow(
  windowSeconds: number,
  medianDt: number,
  order: number,
): number {
  const minRequired = Math.max(5, order + 1);
  const minOdd = minRequired % 2 === 0 ? minRequired + 1 : minRequired;
  const rawSamples = Math.round(windowSeconds / medianDt);
  const odd = rawSamples % 2 === 0 ? rawSamples + 1 : rawSamples;
  return Math.max(odd, minOdd);
}

/**
 * One-sided neighbourhood (seconds) a formula's windowed calls need to produce
 * one output sample. The streamer loads this halo outside a viewport so that at
 * high LOD the edge samples see full neighbourhoods and match the full-series
 * (low-LOD) result, instead of a truncated boundary fit.
 *
 * `sampleIntervalSeconds` is the topic's nominal spacing. A savgol window
 * reaches ≈ half its width; a deriv stencil reaches ±1 (order 1) or ±2
 * (order 2) samples. Halos from every windowed call are summed — an
 * over-estimate for compositions like `deriv(savgol(...))`, but over-reading is
 * harmless. Returns 0 for a non-finite or non-positive interval.
 */
export function computeHaloSeconds(
  constants: ReadonlyMap<ASTNode, readonly number[]>,
  sampleIntervalSeconds: number,
): number {
  if (!Number.isFinite(sampleIntervalSeconds) || sampleIntervalSeconds <= 0) {
    return 0;
  }
  let halfWidthSamples = 0;
  for (const [node, args] of constants) {
    if (node.type !== "CallExpression") {
      continue;
    }
    if (node.callee === "savgol") {
      const [windowSeconds, polynomial] = args;
      const windowSize = resolveSavgolWindow(
        windowSeconds,
        sampleIntervalSeconds,
        polynomial,
      );
      halfWidthSamples += (windowSize - 1) / 2;
    } else if (node.callee === "deriv") {
      const [n] = args;
      halfWidthSamples += n === 2 ? 2 : 1;
    }
  }
  return halfWidthSamples * sampleIntervalSeconds;
}

/** Median sample interval (seconds). Returns 1 when the series has fewer than two samples. */
function medianDelta(timeSeconds: number[]): number {
  if (timeSeconds.length < 2) {
    return 1;
  }
  const deltas = Array.from(
    { length: timeSeconds.length - 1 },
    (_, i) => timeSeconds[i + 1] - timeSeconds[i],
  );
  deltas.sort((a, b) => a - b);
  const mid = Math.floor(deltas.length / 2);
  return deltas.length % 2 === 0
    ? (deltas[mid - 1] + deltas[mid]) / 2
    : deltas[mid];
}
